import { supabase } from './supabaseClient.ts';
import { getAuthUser } from './AuthContext.tsx';

/**
 * Fire-and-forget upsert of a single game's stats to Supabase.
 * Called after game completion (not during in-progress play).
 * Silently fails if user is not authenticated or if the network request fails.
 */
export function syncGameStatsToCloud(
  gameType: string,
  gameNumber: number,
  stats: Record<string, unknown>
): void {
  const user = getAuthUser();
  if (!user) return;

  supabase
    .from('game_stats')
    .upsert(
      {
        user_id: user.id,
        game_type: gameType,
        game_number: gameNumber,
        stats,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,game_type,game_number' }
    )
    .then(({ error }) => {
      if (error) {
        console.error('Failed to sync game stats to cloud:', error);
      }
    });
}

/**
 * When the browser comes back online, push all local data to cloud.
 * This covers the case where a user plays offline while logged in.
 */
let onlineListenerRegistered = false;

export function registerOnlineListener(): void {
  if (onlineListenerRegistered) return;
  onlineListenerRegistered = true;

  window.addEventListener('online', async () => {
    const user = getAuthUser();
    if (!user) return;

    // Dynamically import to avoid circular dependency
    const { fullSyncPush } = await import('./syncService.ts');
    try {
      await fullSyncPush();
    } catch (error) {
      console.error('Failed to sync after coming online:', error);
    }
  });
}

// Register the listener immediately
registerOnlineListener();
